import { isKnown } from './assessmentFacts.js';

// Which stage of life a household is at, for the wording that should not be
// the same for everyone.
//
// The goal sheet has always opened with "Preservation and conservative growth
// of principal", which is the right first goal for someone about to retire and
// the wrong one for almost everyone else: someone 25 years from retirement is
// trying to save enough to retire at all, a business owner is balancing
// liquidity against saving, and a very wealthy household is thinking about
// what passes to whom (Amanda Cook, 2026-10-02). Advisors are meant to rewrite
// the heading per client and almost never do, so the page starts from the
// right one and the advisor can still change it.
//
// The stage is read by rule from what the questionnaire already holds, so
// nothing here needs anyone to write per-client prose. The first rule that
// fits wins, in the order below.

/** Retirement this many years away or fewer counts as near. */
export const NEAR_RETIREMENT_YEARS = 10;
/** With no retirement date to count to, this age or over counts as near. */
export const NEAR_RETIREMENT_AGE = 55;
/** Net worth or investments at or above this is read as high net worth. */
export const HIGH_NET_WORTH = 10000000;

export const PROFILES = {
  nearRetirement: {
    key: 'nearRetirement',
    label: 'At or near retirement',
    goal: 'Preservation and conservative growth of principal',
  },
  accumulating: {
    key: 'accumulating',
    label: 'Building toward retirement',
    goal: 'Saving for retirement and your other lifetime goals',
  },
  businessOwner: {
    key: 'businessOwner',
    label: 'Business owner',
    goal: 'Maintaining liquidity while saving for retirement',
  },
  highNetWorth: {
    key: 'highNetWorth',
    label: 'High net worth',
    goal: 'Efficient transfer of your assets to your heirs',
  },
};

export const PROFILE_ORDER = ['nearRetirement', 'accumulating', 'businessOwner', 'highNetWorth'];

const years = n => `${n} ${n === 1 ? 'year' : 'years'}`;

/**
 * How many years until retirement starts, from the goal sheet's section 2:
 * zero for a household already retired, null when the questionnaire does not
 * say when anyone retires.
 */
export function yearsToRetirement(income) {
  if (!income || income.period === 'unresolved') return null;
  if (income.alreadyThere) return 0;
  if (!Number.isFinite(income.atYear) || !Number.isFinite(income.yearsAway)) return null;
  const thisYear = income.atYear - income.yearsAway;
  return Math.max(0, (income.bridge?.fromYear ?? income.atYear) - thisYear);
}

/**
 * Retirement is far enough off that today's savings say little about it.
 *
 * The assessment assumes no growth and counts only the savings recorded today.
 * A few years from retirement that is close to what the household will retire
 * on. Decades from it, the saving and the growth still to come are most of the
 * answer, so a figure that sets today's balance against retirement spending
 * (a withdrawal rate, a year the money runs out, a required withdrawal in
 * dollars) describes a retirement nobody is going to have. Spending inflated
 * to that year beside savings that are not is the same mistake (Amanda Cook,
 * 2026-10-02). Those findings are withheld for these households.
 */
export function isFarFromRetirement(income) {
  const away = yearsToRetirement(income);
  return away !== null && away > NEAR_RETIREMENT_YEARS;
}

/**
 * The household's stage, and the one-line reason the rule chose it, which the
 * advisor sees when they open the heading to change it.
 *
 * `income` is the goal sheet's section 2, which already knows whether the
 * household is retired and which year retirement starts. `liquid` is the total
 * of the three pools.
 */
export function getHouseholdProfile({ facts, income = null, liquid = 0 } = {}) {
  const people = facts?.people || [];
  const pick = (key, why) => ({ ...PROFILES[key], why });

  const net = isKnown(facts?.net) ? facts.net.value : 0;
  if (Math.max(net, liquid || 0) >= HIGH_NET_WORTH) {
    return pick('highNetWorth', 'net worth or investments are $10 million or more');
  }

  // Retired by the goal sheet's reading of the years, or because everyone in
  // the household says so, which holds even when the years cannot be read.
  const retired = Boolean(income?.alreadyThere) || (people.length > 0 && people.every(p => p.statusRaw === 'Retired'));
  if (!retired && people.some(p => p.statusRaw === 'Self Employed')) {
    return pick('businessOwner', 'someone in the household is self-employed');
  }

  if (retired) return pick('nearRetirement', 'the household is retired');

  const away = yearsToRetirement(income);
  if (away !== null) {
    return away <= NEAR_RETIREMENT_YEARS
      ? pick('nearRetirement', `retirement is ${years(away)} away`)
      : pick('accumulating', `retirement is ${years(away)} away`);
  }

  // No retirement to count to, so the oldest recorded age stands in for it.
  const ages = people.map(p => (isKnown(p.age) ? p.age.value : null)).filter(a => a !== null && a > 0);
  if (ages.length && Math.max(...ages) < NEAR_RETIREMENT_AGE) {
    return pick('accumulating', `everyone is under ${NEAR_RETIREMENT_AGE} and no retirement date is recorded`);
  }
  return pick(
    'nearRetirement',
    ages.length ? `someone is ${NEAR_RETIREMENT_AGE} or over` : 'no age or retirement date is recorded',
  );
}

/**
 * The heading the goal sheet opens with: the advisor's own words when they
 * have written any, otherwise the one the household's stage suggests.
 */
export function getGoalLead(profile, custom) {
  const own = String(custom || '').trim();
  return {
    text: own || profile.goal,
    custom: Boolean(own) && own !== profile.goal,
    suggested: profile.goal,
    stage: profile.label,
    why: profile.why,
  };
}
